import React, { useEffect, useState } from 'react';
import { Search, Plus, RefreshCw, ChevronUp, ChevronDown, Zap } from 'lucide-react';
import api from '../api';
import { Animal } from '../types';

const BREED_LABELS: Record<string, string> = {
  qorabosh: 'Qorabosh', golshtin: 'Golshtin', simmental: 'Simmental',
  abuerdeen: 'Aberdeen', mahalliy: 'Mahalliy',
};

const STATUS_OPTS = [
  { value: '', label: 'Barcha holat' },
  { value: 'faol', label: 'Faol' },
  { value: 'kasal', label: 'Kasal' },
  { value: 'davolanmoqda', label: 'Davolanmoqda' },
  { value: 'sotildi', label: 'Sotildi' },
];

const BREED_OPTS = [
  { value: '', label: 'Barcha zot' },
  { value: 'qorabosh', label: 'Qorabosh' },
  { value: 'golshtin', label: 'Golshtin' },
  { value: 'simmental', label: 'Simmental' },
  { value: 'abuerdeen', label: 'Aberdeen' },
  { value: 'mahalliy', label: 'Mahalliy' },
];

interface AddAnimalModal {
  open: boolean;
}

export default function AnimalsList({ onNavigate }: { onNavigate: (p: string, id?: number) => void }) {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [breedFilter, setBreedFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [sortKey, setSortKey] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const load = () => {
    setLoading(true);
    const params: any = {};
    if (statusFilter) params.status = statusFilter;
    if (breedFilter) params.breed = breedFilter;
    if (search) params.search = search;
    api.getAnimals(params).then(r => { setAnimals(r.data); setLoading(false); });
  };

  useEffect(() => { load(); }, [statusFilter, breedFilter]);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); load(); };

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...animals].sort((a: any, b: any) => {
    let av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ k }: { k: string }) => {
    if (sortKey !== k) return null;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const handleBulkRecommend = async () => {
    setBulkLoading(true);
    await api.bulkRecommend();
    await load();
    setBulkLoading(false);
  };

  return (
    <div className="page-body">
      {/* Filter bar */}
      <div className="filter-bar">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
          <input className="form-input" placeholder="Tag ID yoki ism..." value={search}
            onChange={e => setSearch(e.target.value)} />
          <button type="submit" className="btn btn-primary btn-sm"><Search size={14} /></button>
        </form>
        <select className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="form-select" value={breedFilter} onChange={e => setBreedFilter(e.target.value)}>
          {BREED_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={handleBulkRecommend} disabled={bulkLoading}>
            <Zap size={14} />{bulkLoading ? 'Hisoblanmoqda...' : 'AI Tavsiyalar'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <Plus size={15} /> Yangi mol
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Chorvalar — {animals.length} ta</h3>
          <button className="btn btn-outline btn-sm" onClick={load}><RefreshCw size={13} /></button>
        </div>
        {loading
          ? <div className="loading"><div className="spinner" /></div>
          : <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('tag_id')} style={{ cursor: 'pointer' }}>
                    Tag ID <SortIcon k="tag_id" />
                  </th>
                  <th>Zot</th>
                  <th>Jins</th>
                  <th onClick={() => handleSort('age_months')} style={{ cursor: 'pointer' }}>
                    Yoshi <SortIcon k="age_months" />
                  </th>
                  <th onClick={() => handleSort('latest_weight')} style={{ cursor: 'pointer' }}>
                    Vazn <SortIcon k="latest_weight" />
                  </th>
                  <th onClick={() => handleSort('total_gain')} style={{ cursor: 'pointer' }}>
                    O'sish <SortIcon k="total_gain" />
                  </th>
                  <th>ADG</th>
                  <th>Holat</th>
                  <th>AI Tavsiya</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(a => {
                  const rec = a.latest_recommendation;
                  return (
                    <tr key={a.id}
                      className={rec?.sell_ready ? 'sell-ready-row' : ''}
                      onClick={() => onNavigate('animal-detail', a.id)}>
                      <td><span className="tag-id">{a.tag_id}</span></td>
                      <td>{BREED_LABELS[a.breed] || a.breed}</td>
                      <td>{a.gender === 'erkak' ? '♂' : '♀'} {a.gender}</td>
                      <td>{a.age_months} oy</td>
                      <td><strong>{a.latest_weight} kg</strong></td>
                      <td style={{ color: a.total_gain > 0 ? 'var(--teal)' : 'var(--red)' }}>
                        {a.total_gain > 0 ? '+' : ''}{a.total_gain} kg
                      </td>
                      <td>{a.daily_gain > 0 ? `${a.daily_gain} kg` : '—'}</td>
                      <td><span className={`badge badge-${a.status}`}>{a.status}</span></td>
                      <td>
                        {rec ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>
                              {rec.daily_feed_kg} kg/kun
                            </span>
                            {rec.sell_ready && <span className="badge badge-sell">Sotishga tayyor</span>}
                            {!rec.sell_ready && <span style={{ fontSize: 11, color: 'var(--gray)' }}>
                              {rec.days_remaining} kun qoldi
                            </span>}
                          </div>
                        ) : <span style={{ color: 'var(--gray)', fontSize: 12 }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {sorted.length === 0 && <div className="empty-state"><p>Chorva topilmadi</p></div>}
          </div>
        }
      </div>

      {showAdd && <AddAnimalModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddAnimalModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    tag_id: '', name: '', breed: 'mahalliy', gender: 'erkak',
    birth_date: '', initial_weight: '', status: 'faol', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.createAnimal({ ...form, initial_weight: parseFloat(form.initial_weight) });
      onSaved();
    } catch (err: any) {
      setError(JSON.stringify(err.response?.data || 'Xato yuz berdi'));
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Yangi chorva qo'shish</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="alert alert-red" style={{ marginBottom: 12 }}>{error}</div>}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">RFID Tag ID *</label>
                <input className="form-input" value={form.tag_id} onChange={e => set('tag_id', e.target.value)} required placeholder="TAG-016" />
              </div>
              <div className="form-group">
                <label className="form-label">Ismi</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ixtiyoriy" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Zot *</label>
                <select className="form-select" value={form.breed} onChange={e => set('breed', e.target.value)}>
                  <option value="qorabosh">Qorabosh</option>
                  <option value="golshtin">Golshtin</option>
                  <option value="simmental">Simmental</option>
                  <option value="abuerdeen">Aberdeen Angus</option>
                  <option value="mahalliy">Mahalliy zot</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Jinsi *</label>
                <select className="form-select" value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="erkak">Erkak</option>
                  <option value="urgoq">Urg'oq</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tug'ilgan sana *</label>
                <input type="date" className="form-input" value={form.birth_date} onChange={e => set('birth_date', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Boshlang'ich vazn (kg) *</label>
                <input type="number" className="form-input" value={form.initial_weight} onChange={e => set('initial_weight', e.target.value)} required step="0.1" min="50" placeholder="240" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Izoh</label>
              <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Qo'shimcha ma'lumot..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Bekor qilish</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
